import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import {
  confirmSignUp as cognitoConfirmSignUp,
  getCurrentSession,
  resendConfirmationCode as cognitoResend,
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  signUp as cognitoSignUp,
} from "./cognito";
import { apiFetch } from "../api";

export type AuthUser = {
  sub: string;
  email: string;
  // Server-derived. The catalog management UI uses this to gate itself,
  // and the API re-checks the same condition on every admin-only mutation,
  // so a tampered client value does not grant any extra privilege.
  admin: boolean;
};

type MeResponse = { admin?: unknown };

const fetchAdminFlag = async (): Promise<boolean> => {
  try {
    const me = await apiFetch<MeResponse>("/api/me");
    return me?.admin === true;
  } catch {
    // /api/me is best-effort here. A failure means we render as
    // non-admin — least privilege on the UI side. The API is still
    // authoritative on every mutation.
    return false;
  }
};

type AuthState =
  | { status: "loading" }
  | { status: "signed_out" }
  | { status: "needs_confirm"; email: string }
  | { status: "signed_in"; user: AuthUser };

type AuthContextValue = {
  state: AuthState;
  signUp: (email: string, password: string) => Promise<void>;
  confirmSignUp: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => void;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const userFromIdToken = (idTokenPayload: {
  sub?: unknown;
  email?: unknown;
}): Omit<AuthUser, "admin"> | null => {
  if (
    typeof idTokenPayload.sub === "string" &&
    typeof idTokenPayload.email === "string"
  ) {
    return { sub: idTokenPayload.sub, email: idTokenPayload.email };
  }
  return null;
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "loading" });

  // Cancellation token for async setState calls that fire after a
  // network round-trip. Both the mount-time hydration and signIn() do
  // an initial setState, await /api/me, then setState again to patch
  // the admin flag in. If the provider unmounts (or is replaced) in
  // between, the second setState would warn about updating an
  // unmounted component. The mounted ref short-circuits the second
  // write in that case.
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Hydrate session on mount: if a refresh token is still valid in
  // localStorage, the SDK will refresh transparently. Admin flag comes
  // from /api/me; we render signed-in as soon as we have a baseline user
  // (admin=false) and patch the flag in once the network call returns,
  // so the UI doesn't block on it.
  useEffect(() => {
    let cancelled = false;
    void getCurrentSession().then(async (session) => {
      if (cancelled) return;
      if (!session) return setState({ status: "signed_out" });
      const base = userFromIdToken(session.getIdToken().payload);
      if (!base) return setState({ status: "signed_out" });
      setState({ status: "signed_in", user: { ...base, admin: false } });
      const admin = await fetchAdminFlag();
      if (cancelled) return;
      setState({ status: "signed_in", user: { ...base, admin } });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    await cognitoSignUp(email, password);
    setState({ status: "needs_confirm", email });
  }, []);

  const confirmSignUp = useCallback(
    async (code: string) => {
      if (state.status !== "needs_confirm") {
        throw new Error("No pending confirmation");
      }
      await cognitoConfirmSignUp(state.email, code);
      // After confirmation the user still has to sign in — Cognito doesn't
      // hand back tokens from confirmRegistration. Move them to the sign-in
      // form with the email prefilled.
      setState({ status: "signed_out" });
    },
    [state],
  );

  const resendCode = useCallback(async () => {
    if (state.status !== "needs_confirm") return;
    await cognitoResend(state.email);
  }, [state]);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await cognitoSignIn(email, password);
    const base = userFromIdToken(session.getIdToken().payload);
    if (!base) throw new Error("Token missing required claims");
    setState({ status: "signed_in", user: { ...base, admin: false } });
    const admin = await fetchAdminFlag();
    // Skip the second setState if the component unmounted between the
    // optimistic write and the /api/me round-trip; otherwise React
    // warns about updating an unmounted tree.
    if (!isMounted.current) return;
    setState({ status: "signed_in", user: { ...base, admin } });
  }, []);

  const signOut = useCallback(() => {
    cognitoSignOut();
    setState({ status: "signed_out" });
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ state, signUp, confirmSignUp, resendCode, signIn, signOut }),
    [state, signUp, confirmSignUp, resendCode, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
